import { ProviderDetails } from './appointment.type'
import { Patient } from './PatientInfo.types'
import { INote } from './note.type'
import { ITreatmentPlan, TxPlanStatus } from './treatmentPlan.types'

export const PerioSection = {
  Facial: 'facial',
  Lingual: 'lingual',
} as const

export type PerioSection = (typeof PerioSection)[keyof typeof PerioSection]

export const PERIO_SECTIONS: readonly PerioSection[] = [
  PerioSection.Facial,
  PerioSection.Lingual,
]

export const isPerioSection = (value: unknown): value is PerioSection =>
  typeof value === 'string' &&
  (PERIO_SECTIONS as readonly string[]).includes(value)

export const oppositePerioSection = (section: PerioSection): PerioSection =>
  section === PerioSection.Facial ? PerioSection.Lingual : PerioSection.Facial

export type IPerioChartData = Record<PerioSection, PerioChartSectionData>

// Separated conditions
export type GeneralToothCondition =
  | 'present'
  | 'missing'
  | 'caries'
  | 'recurring_caries'
  | 'caries_D1'
  | 'caries_D2'
  | 'caries_D3'
  | 'caries_E1'
  | 'caries_E2'
  | 'fracture'
  | 'fracture_mesiobuccal'
  | 'fracture_distobuccal'
  | 'fracture_mesiolingual'
  | 'fracture_distolingual'
  | 'fracture_mesiopalatal'
  | 'fracture_distopalatal'
  | 'fracture_vertical_root'
  | 'fracture_horizontal_root'
  | 'crack'
  | 'crack_vertical_crown_buccal'
  | 'crack_vertical_crown_lingual'
  | 'crack_vertical_crown_palatal'
  | 'crack_vertical_crown_occlusal'
  | 'crack_horizontal_crown_buccal'
  | 'crack_horizontal_crown_lingual'
  | 'crack_horizontal_crown_palatal'
  | 'crack_horizontal_crown_occlusal'
  | 'filling'
  | 'attrition'
  | 'attrition_slight'
  | 'attrition_moderate'
  | 'attrition_severe'
  | 'sealants'
  | 'inlays/onlays'
  | 'inlay'
  | 'onlay'
  | 'abrasion'
  | 'abrasion_slight'
  | 'abrasion_moderate'
  | 'abrasion_severe'
  | 'erosion'
  | 'implant'
  | 'bridge'
  | 'bridge_left_bridge'
  | 'bridge_left_abutment'
  | 'bridge_right_bridge'
  | 'bridge_right_abutment'
  | 'bridge_right_left_bridge'
  | 'bridge_right_left_abutment'
  | 'impacted'
  | 'impacted_left'
  | 'impacted_right'
  | 'impacted_down'
  | 'impacted_up'
  | 'veneer'
  | 'veneer_ceramic'
  | 'veneer_composite'
  | 'veneer_zirconia'
  | 'crown'
  | 'crown_zirconia'
  | 'crown_pfm'
  | 'crown_pfz'
  | 'crown_metal'
  | 'crown_ceramic'
  | 'furcation'
  | 'furcation_C1'
  | 'furcation_C2'
  | 'furcation_C3'
  | 'bleeding'
  | 'root_canal'
  | 'pulpotomy'
  | 'pulpectomy'
  | 'apicoectomy'
  | 'periapical'
  | 'periradicular'
  | 'periradicular_slight'
  | 'periradicular_severe'
  | 'periradicular_moderate'
  | 'drifted'
  | 'retainer'
  | 'supernumerary'
  | 'sinus_tract'
  | 'recession'
  | 'recession_class1'
  | 'recession_class2'
  | 'recession_class3'
  | 'recession_class4'
  | 'abutment'

// Surface-oriented codes
export type SurfaceCode =
  | 'm' // Mesial
  | 'd' // Distal
  | 'b' // Generic Buccal
  | 'o' // Occlusal
  | 'p' // Generic Palatal
  | 'i' // Incisal
  | 'l' // Lingual
  | 'mb' // Mesiobuccal
  | 'db' // Distobuccal
  | 'mp' // Mesiopalatal
  | 'dp' // Distopalatal
  | 'cb' // Cervical Buccal
  | 'cp' // Cervical Palatal
  | 'bc' // Buccal Cusp
  | 'bs' // Buccal Surface
  | 'pc' // Palatal Cusp
  | 'ps' // Palatal Surface
  | 'ls' // Lingual Surface
  | 'lc' // Lingual Cusp
  | 'cl' // Cervical Lingual
  | 'dlc' // Disto Lingual Cusp
  | 'mlc' // Mesio Lingual Cusp
  | 'mifp' // Mesio Incisal Facial Palatal
  | 'difp' // Disto Incisal Facial Palatal
  | 'mifl' // Mesio Incisal Facial Lingual
  | 'difl' // Disto Incisal Facial Lingual
  | 'erosion_buccal' // Erosion Buccal
  | 'erosion_palatal' // Erosion Palatal
  | 'erosion_lingual' // Erosion Lingual
  | 'inlay_m' // Inlay Mesial
  | 'inlay_d' // Inlay Distal
  | 'inlay_o' // Inlay Occlusal
  | 'inlay_gold' // Inlay Gold Material
  | 'inlay_zirconia' // Inlay Zirconia Material
  | 'inlay_ceramic' // Inlay Ceramic Material
  | 'onlay_m' // Onlay Mesial
  | 'onlay_d' // Onlay Distal
  | 'onlay_o' // Onlay Occlusal
  | 'onlay_b' // Onlay Buccal
  | 'onlay_p' // Onlay Palatal
  | 'onlay_gold' // Onlay Gold Material
  | 'onlay_zirconia' // Onlay Zirconia Material
  | 'onlay_ceramic' // Onlay Ceramic Material
  | 'drifted_b' // Drifted Buccal
  | 'drifted_m' // Drifted Mesial
  | 'drifted_d' // Drifted Distal
  | 'drifted_p' // Drifted Palatal
  | 'drifted_l' // Drifted Lingual
  | 'filling_ceramic' // Filling Ceramic Material
  | 'filling_composite' // Filling Composite Material
  | 'filling_gold' // Filling Gold Material
  | 'filling_amalgam' // Filling Amalgam Material
  | 'filling_temporary' // Filling Temporary Material
  | 'filling_m' // Filling Mesial
  | 'filling_d' // Filling Distal
  | 'filling_o' // Filling Occlusal
  | 'filling_i' // Filling Incisal
  | 'filling_b' // Filling Buccal
  | 'filling_p' // Filling Palatal
// Union of both
export type ToothCondition = GeneralToothCondition | SurfaceCode

export interface PerioChartSectionData {
  PD: string[]
  GM: string[]
  CAL: string[]
  Bleeding: string[]
  Calculus: string[]
  Supp: string[]
  Furcation: string[]
  Mobility: string[]
}

export interface PerioHistoryEntry {
  chart_id: string
  date: string
  provider_id: string
  patient_id: string
  title: string
  description: string
  perio_data: IPerioChartData
  patient?: Patient
  provider?: ProviderDetails
  date_time_created_at?: string
  date_time_updated_at?: string
}

export interface DentalChartHistoryEntry {
  chart_id: string
  date: string
  tooth_surface: string
  condition: string
  provider_id: string
  patient_id: string
  title: string
  description: string
  date_time_created_at?: string
  date_time_updated_at?: string
}

export interface TreatmentPlanItem {
  code: string
  description: string
  surface: string
  charges: number
  insurance: number
  adjustments: number
  outstanding: number
}

export interface TreatmentPhase {
  name: string
  scheduled_date: string
  status: TxPlanStatus
  items: TreatmentPlanItem[]
}

export interface TreatmentPlan {
  tp_id: string
  patient_id: string
  provider_id: string
  clinic_id: string
  insurance_plan_id?: string | null
  tp_heading: string
  tp_note?: string
  isDraft: boolean
  tp_type?: string
  discount?: number
  phases: TreatmentPhase[]
  provider?: ProviderDetails
  patient?: Patient
}

export interface IChartingData {
  perio_charts: IPerioChart[]
  dental_charts: IDentalChart[]
  treatment_plans: ITreatmentPlan[]
}

export interface IPerioChart {
  chart_id: string
  patient_id: string
  provider_id: string
  clinic_id: string
  title: string
  description: string
  perio_data: IPerioChartData
  patient?: Patient
  provider?: ProviderDetails
  notes?: INote[]
  created_by?: string
  updated_by?: string
  date_time_created_at?: string
  date_time_updated_at?: string
}

export interface IDentalChart {
  chart_id: string
  patient_id: string
  provider_id: string
  clinic_id: string
  title: string
  description: string
  dental_data: IDentalData[]
  patient?: Patient
  provider?: ProviderDetails
  notes?: INote[]
  created_by?: string
  updated_by?: string
  date_time_created_at?: string
  date_time_updated_at?: string
}

export interface IDentalData {
  tooth_number: string
  condition: string
  type: string | null
  material: string | null
  surface: string | null
  recurrent: boolean
}

export type ApplyConditionPayload = Pick<
  IDentalData,
  'condition' | 'type' | 'material' | 'surface'
>

export interface PerioToothChart {
  id: string
  patient_id: string
  provider_id: string
  title: string
  description: string
  data?: IPerioChartData
}

export interface ChartHistory {
  id: string
  date: string
  provider_id: string
  title: string
  tp_note: string
}
